import { useCallback, useContext, useEffect, useRef } from 'react';
import { useCmsContent } from '@sentara-health/sentara-api-hooks-core';
import i18next from 'i18next';
import { isAvMed, GlobalContext } from '../../context';
import { getCache } from '../utils/CacheStorageUtils';

type CmsApiLanguage = 'en' | 'es-US';
type CmsI18nLanguage = 'en-US' | 'es';
type CmsContentPayload = Record<string, unknown>;
type CmsBrand = 'shp' | 'avmed';

const getApiLanguage = (lng?: string): CmsApiLanguage =>
  lng?.toLowerCase().startsWith('es') ? 'es-US' : 'en';

const getI18nLanguage = (lng?: string): CmsI18nLanguage =>
  lng?.toLowerCase().startsWith('es') ? 'es' : 'en-US';

const getCmsBrand = (): CmsBrand => (isAvMed() ? 'avmed' : 'shp');

const applyCmsContent = (
  language: CmsI18nLanguage,
  content: CmsContentPayload
) => {
  i18next.addResourceBundle(language, 'translation', content, true, true);
};

const CmsContentLoader = () => {
  const { getFeature } = useContext(GlobalContext);
  const isCmsContentEnabled = getFeature?.CMSSiteCore ?? false;
  const { getCmsContent } = useCmsContent();
  const getCmsContentRef = useRef(getCmsContent);
  getCmsContentRef.current = getCmsContent;
  const lastLoadedKeyRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  const currentBrand = getCmsBrand();

  // Helper function to remove the "Common" wrapper from the CMS content response, if present. 
  // This ensures that the content is structured correctly for i18next.

  const removeCommonWrapper = (data: CmsContentPayload): CmsContentPayload =>
  Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      value && typeof value === "object" && "Common" in value
        ? (value as Record<string, unknown>).Common
        : value,
    ])
  );

  const loadCmsContent = useCallback(
    async (apiLanguage: CmsApiLanguage, brand: CmsBrand) => {
      const loadKey = `${brand}:${apiLanguage}`;

      if (lastLoadedKeyRef.current === loadKey || inFlightRef.current) {
        return;
      }

      inFlightRef.current = true;
      try {
        const response = await getCmsContentRef.current({
          brand,
          language: apiLanguage,
        }, getCache());
        const { data: cmsResponseData, error: apiError } = response ?? {};

        const modifiedCmsResponse = cmsResponseData ? removeCommonWrapper(cmsResponseData) : undefined;

        if (apiError && !modifiedCmsResponse) {
          return;
        }

        if (modifiedCmsResponse && Object.keys(modifiedCmsResponse).length > 0) {
          const i18nLanguage = getI18nLanguage(apiLanguage);        
          applyCmsContent(i18nLanguage, modifiedCmsResponse);
          lastLoadedKeyRef.current = loadKey;
          await i18next.changeLanguage(i18next.language);
        }
      } finally {
        inFlightRef.current = false;
      }
    },
    []
  );

  useEffect(() => {
    if (!isCmsContentEnabled) return;

    void loadCmsContent(getApiLanguage(i18next.language), currentBrand);

    const onLanguageChanged = (lng: string) => {
      void loadCmsContent(getApiLanguage(lng), currentBrand);
    };

    i18next.on('languageChanged', onLanguageChanged);
    return () => {
      i18next.off('languageChanged', onLanguageChanged);
    };
  }, [loadCmsContent, isCmsContentEnabled]);

  return null;
};

export default CmsContentLoader;
